import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, MapPin, Sparkles, Briefcase, PartyPopper, HeartHandshake, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import FeaturedPartners from "@/components/FeaturedPartners";
import EventBrandMentions from "@/components/EventBrandMentions";

const WHEN_TABS = [
  { v: "", l: "Tout" },
  { v: "today", l: "Aujourd'hui" },
  { v: "weekend", l: "Ce week-end" },
  { v: "week", l: "Cette semaine" },
  { v: "month", l: "Ce mois-ci" },
];
const CATS = [
  { v: "concert", l: "Concert" }, { v: "spectacle", l: "Spectacle" },
  { v: "marche", l: "Marché" }, { v: "animation", l: "Animation" },
  { v: "sport", l: "Sport" }, { v: "expo", l: "Exposition" },
  { v: "fete", l: "Fête" }, { v: "atelier", l: "Atelier" }, { v: "autre", l: "Autre" },
];

export default function EventsPage() {
  const [mode, setMode] = useState("public"); // "public" | "pro" | "asso"
  const [when, setWhen] = useState("");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");
  const [cities, setCities] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [near, setNear] = useState("");
  const [locating, setLocating] = useState(false);
  const [extraOneoff, setExtraOneoff] = useState([]);
  const [oneoffHasMore, setOneoffHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const reqIdRef = useRef(0);
  useSeo({
    title: "Sortir en Eure-et-Loir — l'agenda local | PROXIreduc-28",
    description: "Concerts, marchés, expos et sorties près de chez vous. Suivez l'agenda local et ne manquez plus rien : inscription gratuite.",
    canonicalPath: "/sortir",
    image: "/og-sortir.jpg",
  });

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || []));
  }, []);

  useEffect(() => {
    document.title = "Sortir en Eure-et-Loir | Agenda local | PROXIreduc-28";
    const desc = "Agenda des événements, marchés, concerts et animations à Chartres et en Eure-et-Loir.";
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    md.setAttribute("content", desc);
  }, []);

  const load = async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const p = new URLSearchParams();
    p.set("scope", mode);
    if (when) p.set("when", when);
    if (city) p.set("city", city);
    if (mode === "public" && cat) p.set("category", cat);
    if (near) p.set("near", near);
    try {
      const { data } = await api.get(`/public/events?${p.toString()}`);
      if (myId === reqIdRef.current) {
        setItems(data || []);
        setExtraOneoff([]);
        const baseOneoff = (data || []).filter((e) => !isRecurring(e)).length;
        setOneoffHasMore(!near && baseOneoff >= 60);
      }
    } catch { if (myId === reqIdRef.current) setItems([]); }
    finally { if (myId === reqIdRef.current) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, when, city, cat, near]);

  // « Voir plus » : charge la page suivante de sorties ponctuelles (colonne de gauche)
  const loadMoreOneoff = async () => {
    setLoadingMore(true);
    const p = new URLSearchParams();
    p.set("scope", mode);
    if (when) p.set("when", when);
    if (city) p.set("city", city);
    if (mode === "public" && cat) p.set("category", cat);
    p.set("kind", "oneoff");
    const loaded = items.filter((e) => !isRecurring(e)).length + extraOneoff.length;
    p.set("offset", String(loaded));
    try {
      const { data } = await api.get(`/public/events?${p.toString()}`);
      const rows = data || [];
      setExtraOneoff((prev) => [...prev, ...rows]);
      setOneoffHasMore(rows.length >= 60);
    } catch { toast.error("Impossible de charger plus de sorties."); }
    finally { setLoadingMore(false); }
  };

  const evLoc = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(evLoc.search);
    if (sp.get("when")) setWhen(sp.get("when"));
    if (sp.get("pro") === "1" || sp.get("scope") === "pro") setMode("pro");
    else if (sp.get("scope") === "asso") setMode("asso");
    else setMode("public");
  }, [evLoc.search]);

  // Un événement est "récurrent" si sa date s'étale sur plusieurs semaines/mois
  // (ex. marché hebdomadaire ou expo saisonnière). Sinon il est ponctuel (du jour).
  const DAY_MS = 86400000;
  const isRecurring = (e) => {
    if (!e.start_date || !e.end_date) return false;
    const s = new Date(e.start_date), en = new Date(e.end_date);
    if (isNaN(s) || isNaN(en)) return false;
    return (en - s) / DAY_MS >= 14;
  };
  // Un événement "se déroule aujourd'hui" si sa plage couvre la journée courante
  const isToday = (e) => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const s = new Date(e.start_date);
    const en = e.end_date ? new Date(e.end_date) : s;
    return s <= end && en >= start;
  };
  // Libellé de régularité affiché sur les cartes récurrentes
  const recurrenceLabel = (e) => {
    if (e.category === "marche") return "Chaque semaine";
    const s = new Date(e.start_date), en = new Date(e.end_date);
    if ((en - s) / DAY_MS >= 180) return "Toute l'année";
    return "Rendez-vous régulier";
  };
  const recurringItems = items.filter(isRecurring);
  // Colonne 1 : ponctuels du jour + de la semaine (page de base + pages « Voir plus »),
  // dédupliqués par id, avec ceux du jour en premier.
  const oneoffItems = (() => {
    const seen = new Set();
    return [...items.filter((e) => !isRecurring(e)), ...extraOneoff]
      .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
      .sort((a, b) => {
        const ta = isToday(a) ? 0 : 1, tb = isToday(b) ? 0 : 1;
        if (ta !== tb) return ta - tb;
        return new Date(a.start_date) - new Date(b.start_date);
      });
  })();

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible sur cet appareil."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setNear(`${pos.coords.latitude},${pos.coords.longitude}`); setLocating(false); toast.success("Sorties les plus proches en premier"); },
      () => { setLocating(false); toast.error("Impossible de vous localiser."); },
      { timeout: 8000 }
    );
  };

  return (
    <div data-testid="events-page">
      <section className="bg-gradient-to-br from-rose-50 via-white to-amber-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          {mode === "pro" ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-800 text-xs font-bold uppercase tracking-widest">
                <Briefcase size={14}/> Agenda professionnel
              </div>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold text-slate-900" data-testid="events-title">
                Événements <span className="text-[#E05638]">pros</span> en Eure-et-Loir
              </h1>
              <p className="mt-2 text-slate-600 md:text-lg">
                Salons, foires, rencontres d'affaires et événements d'entreprises près de chez vous.
              </p>
            </>
          ) : mode === "asso" ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-widest">
                <HeartHandshake size={14}/> Agenda associatif
              </div>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold text-slate-900" data-testid="events-title">
                Événements des <span className="text-emerald-700">associations</span>
              </h1>
              <p className="mt-2 text-slate-600 md:text-lg">
                Les rendez-vous proposés par les clubs et associations locales d'Eure-et-Loir.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 border border-rose-200 text-rose-800 text-xs font-bold uppercase tracking-widest">
                <CalendarDays size={14}/> Agenda local
              </div>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-extrabold text-slate-900" data-testid="events-title">
                Sortir en <span className="text-[#E05638]">Eure-et-Loir</span>
              </h1>
              <p className="mt-2 text-slate-600 md:text-lg">
                Marchés, concerts, animations, expositions et fêtes près de chez vous.
              </p>
            </>
          )}

          {/* Bascule Sorties / Pros */}
          <div className="mt-5 inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-sm" data-testid="events-mode-toggle">
            <button onClick={() => { setMode("public"); setCat(""); }} data-testid="events-mode-public"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === "public" ? "bg-[#E05638] text-white" : "text-slate-600 hover:text-slate-900"}`}>
              <PartyPopper size={15}/> Sorties
            </button>
            <button onClick={() => { setMode("pro"); setCat(""); }} data-testid="events-mode-pro"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === "pro" ? "bg-indigo-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              <Briefcase size={15}/> Événements pros
            </button>
            <button onClick={() => { setMode("asso"); setCat(""); }} data-testid="events-mode-asso"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${mode === "asso" ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              <HeartHandshake size={15}/> Associatif
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs quand */}
        <div className="flex flex-wrap gap-2" data-testid="events-when-tabs">
          {WHEN_TABS.map((w) => (
            <button key={w.v || "all"} onClick={() => setWhen(w.v)}
                    data-testid={`events-when-${w.v || "all"}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border ${when === w.v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
              {w.l}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button onClick={locateMe} disabled={locating} data-testid="events-nearby"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${near ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400"}`}>
            <MapPin size={13}/> {locating ? "Localisation…" : near ? "Autour de moi ✓" : "Autour de moi"}
          </button>
          {near && <button onClick={() => setNear("")} data-testid="events-nearby-clear" className="text-xs text-slate-500 hover:text-[#E05638]">Réinitialiser</button>}
        </div>

        <div className="mt-3 grid md:grid-cols-3 gap-3">
          <Select value={city} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="events-city"><SelectValue placeholder="Toutes les villes"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {mode === "public" ? (
            <Select value={cat} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="events-cat"><SelectValue placeholder="Toutes catégories"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {CATS.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}
              </SelectContent>
            </Select>
          ) : (
            <div className="hidden md:block" />
          )}
          <div className="flex items-center justify-end text-sm text-slate-500" data-testid="events-count">
            {loading ? "Chargement..." : `${items.length} événement${items.length > 1 ? "s" : ""}`}
          </div>
        </div>

        {!loading && items.length === 0 ? (
          <div className="mt-6 bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="events-empty">
            <Sparkles size={30} className="mx-auto text-slate-300 mb-2"/>
            {mode === "pro"
              ? "Aucun événement professionnel programmé pour le moment. Revenez bientôt !"
              : "Aucun événement ne correspond à vos critères pour le moment."}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="events-columns">
            {/* Colonne 1 — Sorties & événements du jour (ponctuels) */}
            <div data-testid="events-col-oneoff">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b-2 border-[#E05638]">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-rose-100 text-[#E05638]"><PartyPopper size={16}/></span>
                <div>
                  <h2 className="font-heading font-extrabold text-slate-900 text-lg leading-tight">Sorties & événements du jour / de la semaine</h2>
                  <p className="text-xs text-slate-500">{oneoffItems.length} événement{oneoffItems.length > 1 ? "s" : ""} · ceux du jour en premier</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {oneoffItems.map((e) => (<EventCard key={e.id} event={e}/>))}
              </div>
              {oneoffHasMore && (
                <div className="mt-5 flex justify-center">
                  <Button variant="outline" onClick={loadMoreOneoff} disabled={loadingMore}
                          data-testid="events-oneoff-load-more"
                          className="rounded-full border-[#E05638] text-[#E05638] hover:bg-rose-50 font-bold">
                    {loadingMore ? "Chargement…" : "Voir plus de sorties"}
                  </Button>
                </div>
              )}
              {oneoffItems.length === 0 && (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-400 text-sm" data-testid="events-oneoff-empty">
                  Aucune sortie ponctuelle pour ces critères.
                </div>
              )}
            </div>

            {/* Colonne 2 — Rendez-vous récurrents (marchés, expos saisonnières…) */}
            <div data-testid="events-col-recurring">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b-2 border-emerald-500">
                <span className="grid place-items-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700"><Repeat size={16}/></span>
                <div>
                  <h2 className="font-heading font-extrabold text-slate-900 text-lg leading-tight">Rendez-vous récurrents</h2>
                  <p className="text-xs text-slate-500">Marchés, brocantes & rendez-vous réguliers · {recurringItems.length}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {recurringItems.map((e) => (<EventCard key={e.id} event={e} recurrenceLabel={recurrenceLabel(e)}/>))}
              </div>
              {recurringItems.length === 0 && (
                <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-400 text-sm" data-testid="events-recurring-empty">
                  Aucun rendez-vous récurrent pour ces critères.
                </div>
              )}
            </div>
          </div>
        )}

        <FeaturedPartners limit={6} testId="events-featured-partners" />
        <div className="mt-6">
          <EventBrandMentions />
        </div>
      </section>
    </div>
  );
}
